import { type Tab, Tabset } from "core/Controls/Tabset";
import { Animation, AnimationEasing, mustBeHTMLElement, mustBeHTMLElementOfType } from "silver";

const CLASS_ROOT           = "tabset";
const CLASS_TABS           = "tabset_tabs";
const CLASS_TAB            = "tabset_tab";
const CLASS_TAB_ACTIVE     = "-active";
const CLASS_ROCKER         = "tabset_rocker";
const CLASS_ROCKER_ACTIVE  = "-active";
const CLASS_ROCKER_LEFT    = "-left";
const CLASS_ROCKER_RIGHT   = "-right";
const CLASS_OVERFLOW       = "-overflow";
const CLASS_CONTENT        = "tabset_content";
const CLASS_CONTENT_HIDDEN = "-hidden";
const ATTR_ROOT_NAME       = "data-tabset";
const ATTR_CHILD_NAME      = "data-tabset-name";
const ATTR_TABSET_SELECTED = "data-tabset-selected";
const ATTR_TAB_ID          = "data-tabset-tab";
const ATTR_CONTENT_ID      = "data-tabset-tabpanel";

export class StylizedTabset<TabId extends string = string> extends Tabset<TabId> {

	private scrollAnimation?: Animation;

	public constructor(
		element: HTMLElement,
		tablist: HTMLElement,
		private rockerLeft: HTMLButtonElement,
		private rockerRight: HTMLButtonElement
	) {
		super(element, tablist, element);
		this.element.classList.add(CLASS_ROOT);
		tablist.classList.add(CLASS_TABS);
		this.rockerLeft.classList.add(CLASS_ROCKER, CLASS_ROCKER_LEFT);
		this.rockerRight.classList.add(CLASS_ROCKER, CLASS_ROCKER_RIGHT);
		this.rockerLeft.addEventListener("click", () => this.scrollTablist(-200));
		this.rockerRight.addEventListener("click", () => this.scrollTablist(200));
		this.tabList.addEventListener("scroll", () => this.updateOverflowScroll());
		this.tabList.addEventListener("wheel", () => this.cancelScroll());
		window.addEventListener("resize", () => this.updateOverflowSize());
	}

	private cancelScroll(): void {
		if (!this.scrollAnimation) {
			return;
		}
		this.scrollAnimation.stop();
		this.scrollAnimation = undefined;
		this.updateOverflowScroll();
	}

	private scrollTablist(dx: number): Promise<void> {
		if (this.scrollAnimation) {
			this.cancelScroll();
		}
		const start = this.tabList.scrollLeft;
		const limited = Math.max(0, Math.min(start + dx, this.tabList.scrollWidth - this.tabList.offsetWidth));
		dx = limited - start;
		return new Promise((resolve, reject) => {
			const scrollAnimation = this.scrollAnimation = new Animation((e) => {
				if (scrollAnimation != this.scrollAnimation) {
					reject();
				}
				this.tabList.scrollLeft = start + (e.time * dx);
				if (e.progress === 1) {
					this.scrollAnimation = undefined;
					resolve();
				}
			}, 300, AnimationEasing.easeInOutCubic);
			scrollAnimation.start();
		});
	}

	public static makeTabFromContent<TabId extends string = string>(tabId: TabId, tabContent: Node|string, tabpanelContent: Node): Tab<TabId> {
		const tab = document.createElement("a");
		if (typeof tabContent === "string") {
			tab.appendChild(document.createTextNode(tabContent));
		} else {
			tab.appendChild(tabContent);
		}
		const tabpanel = document.createElement("div");
		tabpanel.appendChild(tabpanelContent);

		return { tabId, tab, tabpanel };
	}

	private static makeTabFromDomElement(tabsetId: string, root: HTMLElement, tab: HTMLElement): Tab {
		const tabId = tab.getAttribute(ATTR_TAB_ID) as string;
		const tabpanel: HTMLElement|null = root.querySelector(`[${ATTR_CHILD_NAME}="${tabsetId}"][${ATTR_CONTENT_ID}="${tabId}"]`);
		if (!tabpanel) {
			throw new Error(`No corresponding content for tab "${tabId}"`);
		}
		return { tabId, tab, tabpanel };
	}

	public static make<TabId extends string = string>(rockerLeftTitle: string, rockerRightTitle: string): StylizedTabset<TabId> {

		const iconLeft = document.createElement("i");
		iconLeft.classList.add("mycon", "-pointer-left");
		const rockerLeft = document.createElement("button");
		rockerLeft.append(iconLeft);
		rockerLeft.title = rockerLeftTitle;

		const iconRight = document.createElement("i");
		iconRight.classList.add("mycon", "-pointer-right");
		const rockerRight = document.createElement("button");
		rockerRight.append(iconRight);
		rockerRight.title = rockerRightTitle;

		const tablist = document.createElement("div");

		const element = document.createElement("div");
		element.append(rockerLeft, rockerRight, tablist);
		return new StylizedTabset<TabId>(element, tablist, rockerLeft, rockerRight);
	}

	public static makeFromDomElement(element: HTMLElement): StylizedTabset {
		const name = element.getAttribute(ATTR_ROOT_NAME);
		if (!name) {
			throw new Error("Tabset root missing name");
		}
		const tabset = new StylizedTabset(
			element,
			mustBeHTMLElement(element.querySelector("[role=\"tablist\"]")),
			mustBeHTMLElementOfType(element.querySelector(".tabset_rocker.-left"), "button"),
			mustBeHTMLElementOfType(element.querySelector(".tabset_rocker.-right"), "button")
		);
		const tabContents = element.querySelectorAll(`[${ATTR_CHILD_NAME}="${name}"][${ATTR_TAB_ID}]`) as NodeListOf<HTMLElement>;
		for (let i = 0; i < tabContents.length; i++) {
			const tab = StylizedTabset.makeTabFromDomElement(name, element, tabContents[i]);
			tabset.add(tab);
			if (tabContents[i].getAttribute(ATTR_TABSET_SELECTED) == "1") {
				tabset.showTab(tab.tabId);
			}
		}
		tabset.urlParam = element.dataset.tabsetUrlParam;
		return tabset;
	}

	public add(tab: Tab<TabId>): void {
		tab.tab.classList.add(CLASS_TAB);
		tab.tab.dataset.tabId = tab.tabId;
		tab.tabpanel.classList.add(CLASS_CONTENT, CLASS_CONTENT_HIDDEN, "g_clearfix");
		tab.tabpanel.dataset.tabId = tab.tabId;

		super.add(tab);
	}

	protected updateOverflowSize(): void {
		this.element.classList.toggle(CLASS_OVERFLOW, this.tabList.scrollWidth > this.tabList.offsetWidth);
		this.updateOverflowScroll();
	}

	protected updateOverflowScroll() {
		const canScrollLeft = this.tabList.scrollLeft > 0;
		const canScrollRight = (this.tabList.scrollLeft + this.tabList.offsetWidth) < this.tabList.scrollWidth;

		this.rockerLeft.classList.toggle(CLASS_ROCKER_ACTIVE, canScrollLeft);
		this.rockerLeft.setAttribute("aria-disabled", canScrollLeft ? "false" : "true");
		this.rockerRight.classList.toggle(CLASS_ROCKER_ACTIVE, canScrollRight);
		this.rockerRight.setAttribute("aria-disabled", canScrollRight ? "false" : "true");
	}

	public showTab(tabId: TabId, focus?: boolean): void {
		super.showTab(tabId, focus);
		this.updateOverflowSize();
	}

	protected handleTabStateChange(tab: Tab<TabId>, active: boolean): void {
		tab.tab.classList.toggle(CLASS_TAB_ACTIVE, active);
		tab.tabpanel.classList.toggle(CLASS_CONTENT_HIDDEN, !active);
		super.handleTabStateChange(tab, active);
		this.updateOverflowSize();
	}
}

